import React, { Fragment, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Home,
  Wrench,
  DollarSign,
  TrendingUp,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const formatCurrency = (value) => {
  return "$" + formatNumber(value);
};

const formatNumber = (value, decimals = 0) => {
  if (isNaN(value) || !isFinite(value)) {
    return decimals > 0 ? (0).toFixed(decimals) : "0";
  }
  return value.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

export function ResultsPropertyCards({
  results,
  homeArea,
  renovationTier,
  extraAreaTier,
}) {
  const [isPaymentStructureExpanded, setIsPaymentStructureExpanded] =
    useState(false);

  const renovationTierDisplay =
    renovationTier > 499
      ? "Premium Renovation"
      : renovationTier > 399
        ? "Complete Renovation"
        : renovationTier > 299
          ? "Standard Renovation"
          : "Basic Renovation";

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
      {/* Property Details Card */}
      <Card className="result-card shadow-md">
        <CardHeader className="p-3 sm:p-4 pb-2">
          <CardTitle className="flex items-center gap-2 text-sm sm:text-base md:text-lg">
            <Home className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
            Property Details
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3 sm:p-4 pt-0">
          <div className="space-y-1.5 sm:space-y-2">
            <div className="flex justify-between text-xs sm:text-sm">
              <span className="text-gray-600">Present Value:</span>
              <span className="font-medium">
                {formatCurrency(results.presentValue)}
              </span>
            </div>
            <div className="flex justify-between text-xs sm:text-sm">
              <span className="text-gray-600">Current Price per sq ft:</span>
              <span className="font-medium">
                {formatCurrency(results.currentPriceSqft)}
              </span>
            </div>
            <div className="flex justify-between text-xs sm:text-sm">
              <span className="text-gray-600">Existing Area:</span>
              <span className="font-medium">
                {formatNumber(homeArea)} sq ft
              </span>
            </div>
            <div className="flex justify-between text-xs sm:text-sm">
              <span className="text-gray-600">Total Area:</span>
              <span className="font-medium">
                {formatNumber(results.totalArea)} sq ft
              </span>
            </div>
            <div className="flex justify-between text-xs sm:text-sm">
              <span className="text-gray-600">Current Loan:</span>
              <span className="font-medium">
                {formatCurrency(results.loan)}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Renovation Details Card */}
      <Card className="result-card shadow-md">
        <CardHeader className="p-3 sm:p-4 pb-2">
          <CardTitle className="flex items-center gap-2 text-sm sm:text-base md:text-lg">
            <Wrench className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
            Renovation Details
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3 sm:p-4 pt-0">
          <div className="space-y-1.5 sm:space-y-2">
            <div className="flex justify-between text-xs sm:text-sm">
              <span className="text-gray-600">Renovation Type:</span>
              <span className="font-medium text-right">
                {renovationTierDisplay} - ${renovationTier} per sq ft
              </span>
            </div>
            <div className="flex justify-between text-xs sm:text-sm">
              <span className="text-gray-600">Renovation Cost:</span>
              <span className="font-medium">
                {formatCurrency(results.renovationCost)}
              </span>
            </div>
            <div className="flex justify-between text-xs sm:text-sm">
              <span className="text-gray-600">Extra Area Added:</span>
              <span className="font-medium">
                {formatNumber(results.extraAreaAdded)} sq ft
              </span>
            </div>
            {results.extraAreaAdded > 0 ? (
              <Fragment>
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-gray-600">
                    Extra Area Renovation Type:
                  </span>
                  <span className="font-medium">
                    ${formatNumber(extraAreaTier)} per sq ft
                  </span>
                </div>
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-gray-600">Extra Area Cost:</span>
                  <span className="font-medium">
                    {formatCurrency(results.extraAreaCost)}
                  </span>
                </div>
              </Fragment>
            ) : (
              <Fragment></Fragment>
            )}
            <div className="flex justify-between text-xs sm:text-sm">
              <span className="text-gray-600">
                Total Area after renovation:
              </span>
              <span className="font-medium">
                {formatNumber(results.totalArea)} sq ft
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Flipping Details Card */}
      <Card className="result-card shadow-md">
        <CardHeader className="p-3 sm:p-4 pb-2">
          <CardTitle className="flex items-center gap-2 text-sm sm:text-base md:text-lg">
            <DollarSign className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
            Flipping Details
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3 sm:p-4 pt-0">
          <div className="space-y-1.5 sm:space-y-2">
            <div className="flex justify-between text-xs sm:text-sm">
              <span className="text-gray-600">Upfront Rental Payment:</span>
              <span className="font-medium">
                {formatCurrency(results.upfrontRental)}
              </span>
            </div>

            {/* Clickable Construction Loan Row with Collapsible Payment Structure */}
            <div>
              <button
                type="button"
                onClick={() =>
                  setIsPaymentStructureExpanded(!isPaymentStructureExpanded)
                }
                className="w-full flex justify-between items-center text-xs sm:text-sm hover:bg-gray-50 p-1 rounded transition-colors cursor-pointer"
              >
                <span className="text-gray-600 flex items-center gap-1">
                  Construction Loan:
                  {isPaymentStructureExpanded ? (
                    <ChevronUp className="h-3 w-3 text-gray-500" />
                  ) : (
                    <ChevronDown className="h-3 w-3 text-gray-500" />
                  )}
                </span>
                <span className="font-medium">
                  {formatCurrency(results.constructionLoan)}
                </span>
              </button>

              {isPaymentStructureExpanded && (
                <div className="bg-gray-50 p-2 sm:p-3 rounded mt-1 ml-2 animate-in slide-in-from-top-2 duration-200">
                  <p className="text-xs font-medium text-gray-700 mb-1.5">
                    Payment Structure:
                  </p>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        Upfront ({results.upfrontPaymentPct}%):
                      </span>
                      <span className="font-medium">
                        {formatCurrency(results.upfrontAmount)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        {results.paymentParts} Installments of:
                      </span>
                      <span className="font-medium">
                        {formatCurrency(results.amountPerPart)} each
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-between text-xs sm:text-sm">
              <span className="text-gray-600">Loan Interest for Investor:</span>
              <span className="font-medium">
                {formatCurrency(results.loanInterest)}
              </span>
            </div>
            <div className="flex justify-between text-xs sm:text-sm">
              <span className="text-gray-600">Guaranteed Buyer Return:</span>
              <span className="font-medium">
                {formatCurrency(results.guaranteedReturn)}
              </span>
            </div>
            <div className="flex justify-between text-xs sm:text-sm">
              <span className="text-gray-600">Agent Commission (6%):</span>
              <span className="font-medium">
                {formatCurrency(results.agentCommission)}
              </span>
            </div>
            <div className="flex justify-between text-xs sm:text-sm">
              <span className="text-gray-600">Closing Costs (2%):</span>
              <span className="font-medium">
                {formatCurrency(results.closingCosts)}
              </span>
            </div>
            <div className="flex justify-between text-xs sm:text-sm">
              <span className="text-gray-600">Total Flipping Cost:</span>
              <span className="font-medium">
                {formatCurrency(results.totalFlippingCost)}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Market Analysis Card */}
      <Card className="result-card shadow-md">
        <CardHeader className="p-3 sm:p-4 pb-2">
          <CardTitle className="flex items-center gap-2 text-sm sm:text-base md:text-lg">
            <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
            Market Analysis
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3 sm:p-4 pt-0">
          <div className="space-y-1.5 sm:space-y-2">
            <div className="flex justify-between text-xs sm:text-sm">
              <span className="text-gray-600">
                Current value (est. / list):
              </span>
              <span className="font-medium">
                {formatCurrency(results.presentValue)}
              </span>
            </div>
            <div className="flex justify-between text-xs sm:text-sm">
              <span className="text-gray-600">Flipping Cost:</span>
              <span className="font-medium">
                {formatCurrency(results.totalFlippingCost)}
              </span>
            </div>
            <div className="flex justify-between text-xs sm:text-sm">
              <span className="text-gray-600">
                Total (current value + flipping cost):
              </span>
              <span className="font-medium">
                {formatCurrency(results.totalInvestment)}
              </span>
            </div>
            <div className="flex justify-between text-xs sm:text-sm">
              <span className="text-gray-600">Market Value (per sq ft):</span>
              <span className="font-medium">
                {formatCurrency(results.marketValue)}
              </span>
            </div>
            <div className="flex justify-between text-xs sm:text-sm">
              <span className="text-gray-600">Estimated Sale Price:</span>
              <span className="font-medium color-primary text-700">
                {formatCurrency(results.estimatedSalePrice)}
              </span>
            </div>
            <div className="flex justify-between text-sm sm:text-base pt-2 border-t border-dashed">
              <span className="text-gray-700 font-semibold">
                Total Extra Proceeds:
              </span>
              <span
                className={`font-bold ${results.totalProfit >= 0 ? "text-green-600" : "text-red-600"}`}
              >
                {formatCurrency(results.totalProfit)}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
